import { ReactNode } from 'react';
import { UserMenu } from '../../components/UserMenu';
import type { PoseTimerState } from '@modelcast/types';
import { Wordmark } from '../../components/Wordmark';

export type SessionRole = 'host' | 'model' | 'artist';

interface SessionHeaderProps {
  roomCode: string;
  roomName?: string;
  role: SessionRole;
  poseState?: PoseTimerState;
  remaining?: number;
  elapsed?: number;
  onSettings?: () => void;
  /** Optional content to render before the spacer (e.g., ViewToggle) */
  centerContent?: ReactNode;
  /** Optional content to render before the user button (e.g., pose controls) */
  rightContent?: ReactNode;
}

export function SessionHeader({
  roomCode,
  roomName,
  role,
  poseState,
  remaining = 0,
  elapsed = 0,
  onSettings,
  centerContent,
  rightContent,
}: SessionHeaderProps) {
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Build status text
  const getStatusText = () => {
    if (!poseState?.isActive) return 'pending';
    if (poseState.isBreak) return 'on break';
    return 'live';
  };

  const isHost = role === 'host';

  return (
    <header className="h-12 flex-shrink-0 flex items-center gap-2 sm:gap-4 px-3 sm:px-5 border-b border-line chrome-gradient">
      {/* Logo */}
      <Wordmark className="text-[15px] sm:text-[17px] flex-shrink-0" />

      {roomName && (
        <>
          <div className="hidden sm:block w-px h-[18px] bg-line" />
          <span className="hidden sm:inline font-sans text-[13px] text-text-dim truncate max-w-[220px] lg:max-w-none">
            {roomName}
          </span>
        </>
      )}

      {/* Optional center content (e.g., ViewToggle) */}
      {centerContent}

      <div className="flex-1 min-w-0" />

      {/* Actions */}
      <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
        {isHost && onSettings && (
          <button
            onClick={onSettings}
            className="btn btn-sm flex items-center gap-1.5 px-2.5 sm:px-3"
            title="View event in a new tab"
            aria-label="View event in a new tab"
          >
            <span>View Event</span>
            <svg
              className="h-3.5 w-3.5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M15 3h6v6" />
              <path d="M10 14 21 3" />
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
            </svg>
          </button>
        )}
        {/* Optional right content (e.g., pose controls) */}
        {rightContent}
        <UserMenu />
      </div>
    </header>
  );
}
